import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import PageHeader from "../../components/PageHeader";
import Pagination from "../../components/Pagination";
import { COLORS } from "../../constants";
import { optionsService } from "../../services/optionsService";
import {
  mobileBroadcastAdminService,
  type MobileBroadcastRow,
  type MobileBroadcastType,
} from "../../services/mobileBroadcastAdminService";

const qkHistory = ["mobile-broadcast", "history"] as const;
const qkUsers = ["mobile-broadcast", "usuarios-options"] as const;

const TYPE_OPTIONS: { value: MobileBroadcastType; label: string }[] = [
  { value: "INFO", label: "Info" },
  { value: "WARN", label: "Aviso" },
  { value: "SUCCESS", label: "Sucesso" },
  { value: "ERROR", label: "Erro" },
];

const TYPE_BADGE: Record<
  MobileBroadcastType,
  { bg: string; text: string; label: string }
> = {
  INFO: { bg: "bg-sky-100", text: "text-sky-800", label: "Info" },
  WARN: { bg: "bg-amber-100", text: "text-amber-900", label: "Aviso" },
  SUCCESS: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Sucesso" },
  ERROR: { bg: "bg-red-100", text: "text-red-800", label: "Erro" },
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
}

type UsuarioOption = {
  id: number;
  nome: string;
  email: string;
  ativo?: boolean;
};

const MensagensMobile: React.FC = () => {
  const queryClient = useQueryClient();

  const [content, setContent] = useState("");
  const [type, setType] = useState<MobileBroadcastType>("INFO");
  const [durationSeconds, setDurationSeconds] = useState(10);
  const [playSound, setPlaySound] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [userFilter, setUserFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const usersQuery = useQuery({
    queryKey: qkUsers,
    queryFn: () => optionsService.getUsuarios(),
  });

  const historyQuery = useQuery({
    queryKey: [...qkHistory, currentPage],
    queryFn: () =>
      mobileBroadcastAdminService.list({
        page: currentPage,
        limit: itemsPerPage,
      }),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      mobileBroadcastAdminService.create({
        content: content.trim(),
        type,
        durationSeconds,
        playSound,
        usuarioIds: selectedIds,
      }),
    onSuccess: async () => {
      toast.success("Mensagem enviada para o app mobile.");
      setContent("");
      setSelectedIds([]);
      await queryClient.invalidateQueries({ queryKey: qkHistory });
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao enviar mensagem.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => mobileBroadcastAdminService.delete(id),
    onSuccess: async () => {
      toast.success("Mensagem excluída.");
      await queryClient.invalidateQueries({ queryKey: qkHistory });
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao excluir mensagem.");
    },
  });

  const usuarios = useMemo(() => {
    const list = (usersQuery.data ?? []) as UsuarioOption[];
    return list.filter((u) => u.ativo !== false);
  }, [usersQuery.data]);

  const filteredUsuarios = useMemo(() => {
    const q = userFilter.trim().toLowerCase();
    if (!q) return usuarios;
    return usuarios.filter(
      (u) =>
        u.nome.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [usuarios, userFilter]);

  const toggleUser = useCallback((id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const selectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const u of filteredUsuarios) next.add(u.id);
      return Array.from(next);
    });
  }, [filteredUsuarios]);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const canSubmit =
    content.trim().length > 0 &&
    selectedIds.length > 0 &&
    durationSeconds >= 3 &&
    durationSeconds <= 120;

  const historyRows = historyQuery.data?.data ?? [];
  const totalItems = historyQuery.data?.total ?? 0;
  const totalPages = historyQuery.data?.totalPages ?? 0;

  const handleDelete = useCallback(
    (row: MobileBroadcastRow) => {
      if (
        !window.confirm(
          "Excluir esta mensagem do histórico? Quem ainda não viu no app deixará de recebê-la."
        )
      ) {
        return;
      }
      deleteMutation.mutate(row.id);
    },
    [deleteMutation]
  );

  return (
    <main className="mx-auto max-w-[1200px] px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Mensagens mobile (app)"
        description="Envie avisos para um ou vários logins. A mensagem aparece somente no aplicativo mobile, com tipo visual, tempo em tela e opção de alerta sonoro."
      />

      <section className="mb-10 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-neutral-900">
          Nova mensagem
        </h2>
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-4">
            <label className="block text-sm font-medium text-neutral-700">
              Conteúdo
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={5}
                maxLength={2000}
                placeholder="Digite a mensagem que aparecerá no app…"
                className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-neutral-500">
                {content.length}/2000
              </span>
            </label>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-neutral-700">
                Tipo
                <select
                  value={type}
                  onChange={(e) =>
                    setType(e.target.value as MobileBroadcastType)
                  }
                  className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                >
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-sm font-medium text-neutral-700">
                Tempo em tela (segundos)
                <input
                  type="number"
                  min={3}
                  max={120}
                  value={durationSeconds}
                  onChange={(e) =>
                    setDurationSeconds(parseInt(e.target.value, 10) || 3)
                  }
                  className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                />
              </label>
            </div>

            <label className="flex items-center gap-2 text-sm text-neutral-800">
              <input
                type="checkbox"
                checked={playSound}
                onChange={(e) => setPlaySound(e.target.checked)}
                className="h-4 w-4 rounded border-neutral-300"
              />
              Reproduzir alerta sonoro no dispositivo
            </label>

            <button
              type="button"
              disabled={!canSubmit || createMutation.isPending}
              onClick={() => createMutation.mutate()}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white shadow transition disabled:opacity-60"
              style={{ backgroundColor: COLORS.PRIMARY }}
            >
              {createMutation.isPending ? "Enviando…" : "Enviar mensagem"}
            </button>
          </div>

          <div>
            <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-neutral-700">
                  Destinatários ({selectedIds.length} selecionado
                  {selectedIds.length === 1 ? "" : "s"})
                </p>
                <p className="text-xs text-neutral-500">
                  Selecione um ou mais logins que usam o app mobile.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllFiltered}
                  className="rounded border border-neutral-300 px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
                >
                  Marcar filtrados
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="rounded border border-neutral-300 px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
                >
                  Limpar
                </button>
              </div>
            </div>
            <input
              type="text"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              placeholder="Filtrar por nome ou e-mail…"
              className="mb-2 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
            />
            <div className="max-h-72 overflow-y-auto rounded-md border border-neutral-200">
              {usersQuery.isLoading ? (
                <p className="p-3 text-sm text-neutral-600">Carregando…</p>
              ) : usersQuery.isError ? (
                <p className="p-3 text-sm text-red-600">
                  Não foi possível carregar usuários.
                </p>
              ) : filteredUsuarios.length === 0 ? (
                <p className="p-3 text-sm text-neutral-600">
                  Nenhum usuário encontrado.
                </p>
              ) : (
                <ul className="divide-y divide-neutral-100">
                  {filteredUsuarios.map((u) => (
                    <li key={u.id}>
                      <label className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-neutral-50">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(u.id)}
                          onChange={() => toggleUser(u.id)}
                          className="mt-0.5 h-4 w-4 rounded border-neutral-300"
                        />
                        <span>
                          <span className="block text-sm font-medium text-neutral-900">
                            {u.nome}
                          </span>
                          <span className="block text-xs text-neutral-500">
                            {u.email}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white shadow-sm">
        <div className="border-b border-neutral-200 p-6">
          <h2 className="text-lg font-semibold text-neutral-900">Histórico</h2>
          <p className="mt-1 text-sm text-neutral-600">
            Mensagens enviadas e status de leitura no app. Você pode excluir
            entradas; isso remove a mensagem também para quem ainda não viu.
          </p>
        </div>

        {historyQuery.isLoading ? (
          <p className="p-6 text-neutral-600">Carregando…</p>
        ) : historyQuery.isError ? (
          <p className="p-6 text-red-600">
            Não foi possível carregar o histórico.
          </p>
        ) : historyRows.length === 0 ? (
          <p className="p-6 text-neutral-600">
            Nenhuma mensagem enviada ainda.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto px-2 pb-2">
              <table className="min-w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    <th className="px-3 py-2 font-semibold">Enviada em</th>
                    <th className="px-3 py-2 font-semibold">Tipo</th>
                    <th className="px-3 py-2 font-semibold">Conteúdo</th>
                    <th className="px-3 py-2 font-semibold">Duração</th>
                    <th className="px-3 py-2 font-semibold">Som</th>
                    <th className="px-3 py-2 font-semibold">Destinatários</th>
                    <th className="px-3 py-2 font-semibold">Por</th>
                    <th className="px-3 py-2 font-semibold" />
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((row) => {
                    const badge = TYPE_BADGE[row.type];
                    return (
                      <tr
                        key={row.id}
                        className="border-b border-neutral-100 align-top"
                      >
                        <td className="whitespace-nowrap px-3 py-2 text-xs text-neutral-600">
                          {formatDate(row.createdAt)}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${badge.bg} ${badge.text}`}
                          >
                            {badge.label}
                          </span>
                        </td>
                        <td className="max-w-xs px-3 py-2 text-neutral-800">
                          <p className="line-clamp-3 whitespace-pre-wrap">
                            {row.content}
                          </p>
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {row.durationSeconds}s
                        </td>
                        <td className="px-3 py-2">
                          {row.playSound ? "Sim" : "Não"}
                        </td>
                        <td className="px-3 py-2 text-xs text-neutral-700">
                          <div>
                            {row.dismissedCount}/{row.recipientCount} visto
                            {row.recipientCount === 1 ? "" : "s"}
                          </div>
                          <details className="mt-1">
                            <summary className="cursor-pointer text-primary">
                              Ver lista
                            </summary>
                            <ul className="mt-1 space-y-0.5">
                              {row.recipients.map((r) => (
                                <li key={r.id}>
                                  {r.nome}{" "}
                                  {r.dismissedAt ? (
                                    <span className="text-emerald-700">
                                      (visto)
                                    </span>
                                  ) : (
                                    <span className="text-neutral-500">
                                      (pendente)
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </details>
                        </td>
                        <td className="px-3 py-2 text-xs text-neutral-600">
                          {row.createdBy.nome}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            disabled={deleteMutation.isPending}
                            onClick={() => handleDelete(row)}
                            className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                          >
                            Excluir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalItems > 0 && totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </section>
    </main>
  );
};

export default MensagensMobile;
